import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { Trans, useTranslation } from 'react-i18next';

import { Button, Tooltip, TooltipProps, tooltipClasses } from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import { HelpIcon } from '@repo/ui/Icon';
import styled from 'styled-components';

import { useCheckVerificationsComplete } from 'hooks/useCheckVerificationsComplete';
import { useGetRevenues, useGetRevenuesCumulative } from 'services/hooks/revenue';
import { prefetchWithdrawalsInfo } from 'services/hooks/withdrawal';
import { useModalStackStore, useNoticeModalFlag } from 'store/modalStackStore';
import { LOADING_KEY } from 'utils/promise';
import { translation } from 'utils/translation';

import Card from 'components/card/Card';
import withErrorBoundary from 'components/provider/withErrorBoundary';
import Flex from 'components/util/Flex';

import AvailableChangeBubbleModal from './AvailableChangeBubbleModal';
import AvailableWithdrawalModal from './AvailableWithdrawalModal';
import RevenuePeriodChangeNoticeModal from './RevenuePeriodChangeNoticeModal';
import UnavailableChangeBubbleModal from './UnavailableChangeBubbleModal';
import UnavailableWithdrawalModal from './UnavailableWithdrawalModal';
import { SInformationItem } from '../shared/style';

function RevenueInformationContainer() {
  const { t } = useTranslation();

  return (
    <Card isExpanded>
      <Card.Header>
        <Card.Title>{t('chat.rewards_revenue_information')}</Card.Title>
      </Card.Header>
      <Card.Body>
        <RevenueInformationList />
      </Card.Body>
    </Card>
  );
}

export default withErrorBoundary(RevenueInformationContainer);

function RevenueInformationList() {
  const { t } = useTranslation();

  const { isBankAccountVerificationCompleted, isConvertibleToBubble } = useCheckVerificationsComplete();
  const { data: revenuesInformation, isLoading } = useGetRevenues();
  const { data: cumulative } = useGetRevenuesCumulative();
  const { openModal, closeModal } = useModalStackStore();
  const [cookies] = useCookies();

  // 수익 정보 > 금액 블러 마스킹 조건
  // 1. 계좌 인증이 완료된 유저
  // 2. 본인 인증과 이메일 인증이 완료된 유저 = 버블 전환 가능 유저

  const isBlur = !(isBankAccountVerificationCompleted || isConvertibleToBubble);
  // 모달 노출 조건
  // 1. 이메일 인증과 본인 인증이 끝난 유저
  // 2. 1 번에 해당하며 다시 보지 않기 쿠키가 활성화 되지 않은 유저

  const modalFlag = useNoticeModalFlag();

  useEffect(() => {
    const openFlag = async () => {
      if (!isLoading) {
        if (cookies.USER_SET_REWARD_NOTICE_EXPIRES) {
          closeModal();
        } else if (modalFlag.flag === false) {
          modalFlag.setFlag(true);
          openModal(<RevenuePeriodChangeNoticeModal />, {
            closeOnBackdropClick: true,
            modalWrapperStyle: { width: '320px' },
          });
        }
      }
    };
    openFlag();
  }, [isLoading]);

  return (
    <>
      <Flex direction="column">
        <SInformationItemWrapper>
          <Flex columnGap={4} align="center">
            <span>{t('chat.rewards_revenue_information_total_revenue')}</span>
            <HelpIconButton translateKey={'chat.rewards_total_revenue_time_update_tooltip'} />
          </Flex>
          <span className="bold">{cumulative?.cumulativeRevenueFormat ?? 0}</span>
        </SInformationItemWrapper>
        <SInformationItemWrapper>
          <span>
            {t('chat.rewards_revenue_information_total_withdrawals')}
            {translation('chat.rewards_currency_krw')}
          </span>
          <span className={`bold ${isBlur ? 'blur' : ''}`}>
            {isBlur ? '50,000' : revenuesInformation?.cumulativeWithdrawalFormat}
          </span>
        </SInformationItemWrapper>
        <SInformationItemWrapper>
          <span>
            {t('chat.rewards_revenue_information_last months_withdrawal')}
            {translation('chat.rewards_currency_krw')}
          </span>
          <span className={`bold ${isBlur ? 'blur' : ''}`}>
            {isBlur ? '100,000' : revenuesInformation?.previousMonthRevenueFormat}
          </span>
        </SInformationItemWrapper>
        <SInformationItemWrapper>
          <Flex columnGap={4} align="center">
            <span
              style={{
                wordBreak: 'break-all',
                overflowWrap: 'break-word',
              }}
            >
              {t('chat.rewards_revenue_information_withdrawable_revenue')}
              {translation('chat.rewards_currency_krw')}
            </span>
            <HelpIconButton translateKey={'chat.rewards_revenue_information_withdrawable_revenue_help_text'} />
          </Flex>
          <span className={`bold ${isBlur ? 'blur' : ''}`}>
            {isBlur ? '50,000' : revenuesInformation?.availablePreTaxWithdrawalFormat}
          </span>
        </SInformationItemWrapper>
        <SInformationItemWrapper>
          <Flex columnGap={4} align="center">
            <span
              style={{
                wordBreak: 'break-all',
                overflowWrap: 'break-word',
              }}
            >
              <span>{t('chat.rewards_revenue_information_profit_sharing_rate')}</span>
            </span>
            {!isBlur && (
              <HelpIconButton
                translateKey={`*${t('chat.create_character_multi_images_title')} : ${revenuesInformation?.triggerImageShareRateFormat}`}
              />
            )}
          </Flex>
          <span className={`bold ${isBlur ? 'blur' : ''}`}>{revenuesInformation?.revenueShareRateFormat}</span>
        </SInformationItemWrapper>
      </Flex>
      <SAdditionalInformation align="center" justify="center" columnGap={8}>
        <ChangeBubbleRequestButton />
        <WithdrawalRequestButton />
      </SAdditionalInformation>
    </>
  );
}

const SInformationItemWrapper = styled(SInformationItem)`
  padding: 16px 0;
`;

const SAdditionalInformation = styled(Flex)`
  margin-top: 24px;
  @media ${({ theme }) => theme.device.large} {
    margin-top: 16px;
  }
`;

function HelpIconButton({ translateKey }: { translateKey: string | (string & string[]) }) {
  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };
  return (
    <HelpIconTooltipWrapper
      title={
        <div>
          <Trans i18nKey={translateKey} components={{ br: <br /> }} />
        </div>
      }
      onClick={handleOpen}
      onClose={handleClose}
      onMouseEnter={handleOpen}
      onMouseLeave={handleClose}
      open={open}
    >
      <Button sx={{ width: '20px', height: '24px', minWidth: '20px' }}>
        <HelpIcon size={20} />
      </Button>
    </HelpIconTooltipWrapper>
  );
}

const HelpIconTooltipWrapper = muiStyled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} placement="top" />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
    fontSize: '14px', // 기본 텍스트 크기
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px', // 모바일일 경우 더 작게
    },
  },
}));

export function WithdrawalRequestButton() {
  const { t } = useTranslation();
  const { openModal } = useModalStackStore();
  const { isWithdrawable, isFetched } = useCheckVerificationsComplete();

  const handleClick = () => {
    if (isWithdrawable) {
      openModal(<AvailableWithdrawalModal />, {
        closeOnBackdropClick: true,
        modalWrapperStyle: {
          maxWidth: '560px',
        },
        loading: {
          key: LOADING_KEY.WITHDRAWAL_REQUEST,
        },
      });
    } else {
      openModal(<UnavailableWithdrawalModal />, {
        closeOnBackdropClick: true,
        modalWrapperStyle: {
          width: '360px',
        },
      });
    }
  };

  const handlePrefetchData = () => {
    prefetchWithdrawalsInfo();
  };

  return (
    <SWithdrawalRequestButton onClick={handleClick} disabled={!isFetched} onMouseEnter={handlePrefetchData}>
      {t('chat.rewards_revenue_information_request_a_withdrawal')}
    </SWithdrawalRequestButton>
  );
}

type SWithdrawalRequestButtonProps = {
  $isActive?: boolean;
};
const SWithdrawalRequestButton = styled.button<SWithdrawalRequestButtonProps>`
  padding: 12px 0;
  max-width: 200px;
  width: 100%;
  border-radius: 8px;
  background-color: ${({ theme, $isActive = true }) =>
    $isActive ? theme.color.static.primary : theme.palette.neutral[30]};
  color: ${({ theme }) => theme.color.static.white};
  ${({ theme }) => theme.font.body1_m};
  align-self: stretch;
`;

export function ChangeBubbleRequestButton() {
  const { openModal } = useModalStackStore();
  const { isConvertibleToBubble, isFetched } = useCheckVerificationsComplete();

  const handleClick = () => {
    if (isConvertibleToBubble) {
      openModal(<AvailableChangeBubbleModal />, {
        closeOnBackdropClick: true,
        modalWrapperStyle: {
          maxWidth: '560px',
        },
        loading: {
          key: LOADING_KEY.WITHDRAWAL_REQUEST,
        },
      });
    } else {
      openModal(<UnavailableChangeBubbleModal />, {
        closeOnBackdropClick: true,
        modalWrapperStyle: {
          width: '360px',
        },
      });
    }
  };

  const handlePrefetchData = () => {
    prefetchWithdrawalsInfo();
  };

  return (
    <SChangeBubbleRequestButton onClick={handleClick} onMouseEnter={handlePrefetchData} disabled={!isFetched}>
      {/* 버블전환하기 */}
      {translation('chat.rewards_convert_bubbles_button')}
    </SChangeBubbleRequestButton>
  );
}

type SChangeBubbleRequestButtonProps = {
  $isActive?: boolean;
};
const SChangeBubbleRequestButton = styled.button<SChangeBubbleRequestButtonProps>`
  padding: 12px 0;
  max-width: 200px;
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.palette.neutral[30]};
  background-color: ${({ theme, $isActive = true }) =>
    $isActive ? theme.color.static.white : theme.palette.neutral[15]};
  color: ${({ theme, $isActive }) => ($isActive ? theme.color.text.TXT_1 : theme.color.text.TXT_0)};
  ${({ theme }) => theme.font.body1_m};
  align-self: stretch;
`;
